/**
 * Utility for printing question details to PDF
 * Supports both single and multiple questions
 */

/**
 * Wait for image prefetching to complete
 * @param {boolean} isPrefetching - Current prefetching state
 * @param {number} maxWaitMs - Maximum time to wait in milliseconds
 * @returns {Promise<void>}
 */
const waitForPrefetch = async (isPrefetching, maxWaitMs = 5000) => {
  if (!isPrefetching) return;
  
  const start = Date.now();
  while (isPrefetching && Date.now() - start < maxWaitMs) {
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
};

/**
 * Generate HTML for a single question section
 * @param {Object} question - Question object
 * @param {Object} prefetchedImages - Prefetched image URLs
 * @returns {string} HTML string
 */
const generateQuestionHTML = (question, prefetchedImages = {}) => {
  const questionFile = prefetchedImages.question_file || question.question_file;
  const msFile = prefetchedImages.ms_file || question.ms_file || "https://placehold.co/600x400?text=No+image";
  const placeholderImg = "https://placehold.co/600x400?text=No+image";
  
  const topicsText = question?.topics?.map((t) => t.title).join(", ") || "N/A";
  const subtopicsText = question?.subtopics?.map((st) => st.title).join(", ") || "N/A";
  
  let html = `
    <!-- Question Section -->
    <div class="print-section question-section">
      <h4 style="margin-top: 0">
        Question: Q. ${question?.question_number || ""} ${question?.part || ""}${question?.subpart ? ` (${question?.subpart})` : ""}
      </h4>
      <p style="margin: 0">
        <strong>Paper:</strong> ${question?.paper || "N/A"}, 
        <strong>Year:</strong> ${question?.year || "N/A"}, 
        <strong>Section:</strong> ${question?.section || "N/A"}
      </p>
      <p style="margin: 0">
        <strong>Topics:</strong> ${topicsText}
      </p>
      <p style="margin: 0">
        <strong>Subtopics:</strong> ${subtopicsText}
      </p>
      <p style="margin: 0">
        <strong>Marks:</strong> ${question?.marks || "N/A"}
      </p>
      <img src="${questionFile}" alt="Question" onerror="this.onerror=null;this.src='${placeholderImg}'" />
    </div>

    <!-- Marking Scheme Section -->
    <div class="print-section marking-scheme-section">
      <h4 style="margin-top: 0">
        Marking Scheme for Q. ${question?.question_number || ""}
      </h4>
      <img src="${msFile}" alt="Marking Scheme" onerror="this.onerror=null;this.src='${placeholderImg}'" />
    </div>
  `;

  // Add dependencies if they exist
  if (question.dependencies?.length > 0) {
    question.dependencies.forEach((dep, idx) => {
      const depFile = prefetchedImages.deps?.[dep._id] || dep.file_name || "";
      html += `
        <div class="print-section dependency-section">
          <h4 style="margin-top: 0">
            Dependency ${idx + 1} for Q. ${question?.question_number || ""}
          </h4>
          <img src="${depFile}" alt="Dependency ${idx + 1}" onerror="this.onerror=null;this.src='${placeholderImg}'" />
        </div>
      `;
    });
  }

  return html;
};

/**
 * Generate complete print HTML document
 * @param {string} contentHTML - The main content HTML
 * @param {string} title - Document title
 * @returns {string} Complete HTML document
 */
const generatePrintDocument = (contentHTML, title = "Question Details") => {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        /* Set standard print page size and margins */
        @page {
          size: A4;
          margin: 0.75in;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          color: #333;
          line-height: 1.6;
          margin: 0;
          padding: 0;
          width: 100%;
          height: auto;
        }
        .print-section {
          margin-bottom: 2.5rem;
          page-break-inside: auto;
          break-inside: auto;
          padding-top: 0.5rem;
        }
        .print-section:not(:last-child) {
          page-break-after: auto;
        }
        h4 {
          font-size: 1.3rem;
          color: #7a5d10;
          border-bottom: 1px solid #eee;
          padding-bottom: 0.5rem;
          margin-top: 0;
          margin-bottom: 1rem;
          page-break-after: avoid;
        }
        p {
          margin: 0.2rem 0;
          page-break-inside: avoid;
        }
        strong {
          font-weight: 600;
        }
        img {
          max-width: 100%;
          height: auto;
          display: block;
          margin: 1.5rem 0 2rem 0;
          border: 1px solid #dee2e6;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          page-break-inside: auto;
        }
        /* Separator between multiple questions */
        .question-separator {
          border-top: 3px solid #7a5d10;
          margin: 3rem 0;
          page-break-before: always;
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        ${contentHTML}
      </div>
      <script>
        window.onload = function() {
          setTimeout(() => {
            window.print();
          }, 500);
        }
      </script>
    </body>
    </html>
  `;
};

/**
 * Print single question to PDF
 * @param {Object} question - Question object
 * @param {Object} prefetchedImages - Prefetched image URLs
 * @param {boolean} isPrefetching - Whether images are still being prefetched
 * @returns {Promise<void>}
 */
export const printSingleQuestion = async (question, prefetchedImages = {}, isPrefetching = false) => {
  // Wait for prefetching to complete
  await waitForPrefetch(isPrefetching);

  if (!question) {
    console.error("No question data provided for printing.");
    return;
  }

  const contentHTML = generateQuestionHTML(question, prefetchedImages);
  const title = `Question Details: Q. ${question?.question_number || ""}`;
  const printHTML = generatePrintDocument(contentHTML, title);

  openPrintWindow(printHTML);
};

/**
 * Print multiple questions to PDF
 * @param {Array<Object>} questions - Array of question objects
 * @param {Array<Object>} prefetchedImagesArray - Array of prefetched image objects (one per question)
 * @param {boolean} isPrefetching - Whether images are still being prefetched
 * @returns {Promise<void>}
 */
export const printMultipleQuestions = async (questions, prefetchedImagesArray = [], isPrefetching = false) => {
  // Wait for prefetching to complete
  await waitForPrefetch(isPrefetching);

  if (!questions || questions.length === 0) {
    console.error("No questions provided for printing.");
    return;
  }

  let contentHTML = "";
  
  questions.forEach((question, index) => {
    const prefetchedImages = prefetchedImagesArray[index] || {};
    
    // Add separator between questions (except before first question)
    if (index > 0) {
      contentHTML += '<div class="question-separator"></div>';
    }
    
    contentHTML += generateQuestionHTML(question, prefetchedImages);
  });

  const title = `Question Details: ${questions.length} Question${questions.length > 1 ? 's' : ''}`;
  const printHTML = generatePrintDocument(contentHTML, title);

  openPrintWindow(printHTML);
};

/**
 * Open a new window with print content
 * @param {string} printHTML - Complete HTML document to print
 */
const openPrintWindow = (printHTML) => {
  const printWindow = window.open("", "_blank", "width=800,height=600");
  
  if (!printWindow) {
    console.error("Could not open new window for printing. Check pop-up blockers.");
    alert("Unable to open print window. Please check your pop-up blocker settings.");
    return;
  }

  printWindow.document.write(printHTML);
  printWindow.document.close();
};

/**
 * Helper function to extract content from DOM element for printing
 * This is useful when you have a hidden print section in your component
 * @param {string} selector - CSS selector for the print content wrapper
 * @param {string} title - Document title
 * @returns {void}
 */
export const printFromDOMElement = async (selector, title = "Print Document", isPrefetching = false) => {
  // Wait for prefetching to complete
  await waitForPrefetch(isPrefetching);

  const printContentElement = document.querySelector(selector);
  
  if (!printContentElement) {
    console.error(`Print content element not found: ${selector}`);
    return;
  }

  const contentHTML = printContentElement.innerHTML;
  const printHTML = generatePrintDocument(contentHTML, title);

  openPrintWindow(printHTML);
};
